import { primitiveColors, semanticColors } from "@dfp/isomorphic-design-tokens";

interface BrandPalette {
  fill: string;
  icon: string;
}

interface CustomPalette {
  // Background colors
  bg: string;
  bgSecondary: string;
  bgInverse: string;

  // Surface colors
  surface: string;
  surfaceSecondary: string;
  surfaceTertiary: string;
  surfaceInverse: string;
  surfaceBrand: string;
  surfaceAccent: string;
  surfaceInfo: string;
  surfaceSuccess: string;
  surfaceWarning: string;
  surfaceError: string;

  // Fill colors
  fill: string;
  fillHover: string;
  fillActive: string;
  fillDisabled: string;
  fillSecondary: string;
  fillSecondaryHover: string;
  fillSecondaryActive: string;
  fillTertiaryHover: string;
  fillTertiaryActive: string;
  fillNeutral: string;
  fillNeutralHover: string;
  fillNeutralActive: string;
  fillNeutralDisabled: string;
  fillInverse: string;
  fillBrand: string;
  fillAccent: string;
  fillInfo: string;
  fillSuccess: string;
  fillWarning: string;
  fillError: string;
  fillErrorHover: string;
  fillErrorActive: string;
  fillErrorSecondary: string;
  fillErrorSecondaryHover: string;
  fillErrorSecondaryActive: string;
  fillEmphasis: string;
  fillEmphasisHover: string;
  fillEmphasisActive: string;

  // Text colors
  text: string;
  textSecondary: string;
  textDisabled: string;
  textInverse: string;
  textPrimary: string;
  textAccent: string;
  textBrand: string;
  textInfo: string;
  textSuccess: string;
  textWarning: string;
  textError: string;

  // Link colors
  link: string;
  linkHover: string;
  linkActive: string;
  linkDisabled: string;

  // Icon colors
  icon: string;
  iconSecondary: string;
  iconDisabled: string;
  iconInverse: string;
  iconPrimary: string;
  iconAccent: string;
  iconBrand: string;
  iconInfo: string;
  iconSuccess: string;
  iconWarning: string;
  iconError: string;

  // Border colors
  border: string;
  borderSecondary: string;
  borderHover: string;
  borderActive: string;
  borderDisabled: string;
  borderPrimary: string;
  borderAccent: string;
  borderSecondaryAccent: string;
  borderBrand: string;
  borderSecondaryInfo: string;
  borderSecondarySuccess: string;
  borderSecondaryWarning: string;
  borderSecondaryError: string;
  borderError: string;
  borderEmphasis: string;
  borderEmphasisHover: string;
  borderEmphasisActive: string;
  borderImage: string;

  // Backdrop
  backdrop: string;
}

declare module "@mui/material/styles" {
  interface Palette {
    brand: BrandPalette;
    custom: CustomPalette;
  }

  interface PaletteOptions {
    brand?: Partial<BrandPalette>;
    custom?: Partial<CustomPalette>;
  }
}

export const palette = {
  common: {
    white: primitiveColors.greyscale.white,
    black: primitiveColors.greyscale.black,
  },
  primary: {
    light: semanticColors.textSecondary,
    main: semanticColors.fill,
    contrastText: semanticColors.textInverse,
  },
  secondary: {
    light: semanticColors.fillNeutralHover,
    main: semanticColors.surfaceSecondary,
    dark: semanticColors.surfaceSecondary,
    contrastText: semanticColors.text,
  },
  accent: {
    light: semanticColors.surfaceAccent,
    main: semanticColors.fillAccent,
    dark: semanticColors.fillAccent,
    contrastText: semanticColors.textInverse,
  },
  error: {
    light: semanticColors.surfaceError,
    main: semanticColors.fillError,
    dark: semanticColors.fillErrorHover,
    contrastText: semanticColors.textInverse,
  },
  errorSecondary: {
    light: semanticColors.surfaceError,
    main: semanticColors.fillErrorSecondary,
    dark: semanticColors.fillErrorSecondaryHover,
    contrastText: semanticColors.textError,
  },
  emphasis: {
    main: semanticColors.fillEmphasis,
    dark: semanticColors.fillEmphasisHover,
    contrastText: semanticColors.text,
  },
  success: {
    light: semanticColors.surfaceSuccess,
    main: semanticColors.fillSuccess,
    dark: semanticColors.fillSuccess,
    contrastText: semanticColors.textSuccess,
  },
  warning: {
    light: semanticColors.surfaceWarning,
    main: semanticColors.fillWarning,
    dark: semanticColors.fillWarning,
    contrastText: semanticColors.textWarning,
  },
  info: {
    light: semanticColors.surfaceInfo,
    main: semanticColors.fillInfo,
    dark: semanticColors.fillInfo,
    contrastText: semanticColors.textInfo,
  },
  grey: {
    25: primitiveColors.greyscale[25],
    50: primitiveColors.greyscale[50],
    100: primitiveColors.greyscale[100],
    200: primitiveColors.greyscale[200],
    300: primitiveColors.greyscale[300],
    400: primitiveColors.greyscale[400],
    500: primitiveColors.greyscale[500],
    600: primitiveColors.greyscale[600],
    700: primitiveColors.greyscale[700],
    800: primitiveColors.greyscale[800],
    900: primitiveColors.greyscale[900],
  },
  background: {
    main: semanticColors.bg,
    paper: semanticColors.surface,
    accent: semanticColors.surfaceAccent,
  },
  divider: semanticColors.borderSecondary,
  text: {
    main: semanticColors.text,
    primary: semanticColors.text,
    secondary: semanticColors.textSecondary,
    disabled: semanticColors.textDisabled,
  },
  action: {
    disabled: semanticColors.textDisabled,
    disabledBackground: semanticColors.fillDisabled,
  },
  brand: {
    fill: semanticColors.fillBrand,
    icon: semanticColors.iconBrand,
  },
  custom: {
    // Background colors
    bg: semanticColors.bg,
    bgSecondary: semanticColors.bgSecondary,
    bgInverse: semanticColors.bgInverse,

    // Surface colors
    surface: semanticColors.surface,
    surfaceSecondary: semanticColors.surfaceSecondary,
    surfaceTertiary: semanticColors.surfaceTertiary,
    surfaceInverse: semanticColors.surfaceInverse,
    surfaceBrand: semanticColors.surfaceBrand,
    surfaceAccent: semanticColors.surfaceAccent,
    surfaceInfo: semanticColors.surfaceInfo,
    surfaceSuccess: semanticColors.surfaceSuccess,
    surfaceWarning: semanticColors.surfaceWarning,
    surfaceError: semanticColors.surfaceError,

    // Fill colors
    fill: semanticColors.fill,
    fillHover: semanticColors.fillHover,
    fillActive: semanticColors.fillActive,
    fillDisabled: semanticColors.fillDisabled,
    fillSecondary: semanticColors.fillSecondary,
    fillSecondaryHover: semanticColors.fillSecondaryHover,
    fillSecondaryActive: semanticColors.fillSecondaryActive,
    fillTertiaryHover: semanticColors.fillTertiaryHover,
    fillTertiaryActive: semanticColors.fillTertiaryActive,
    fillNeutral: semanticColors.fillNeutral,
    fillNeutralHover: semanticColors.fillNeutralHover,
    fillNeutralActive: semanticColors.fillNeutralActive,
    fillNeutralDisabled: semanticColors.fillNeutralDisabled,
    fillInverse: semanticColors.fillInverse,
    fillBrand: semanticColors.fillBrand,
    fillAccent: semanticColors.fillAccent,
    fillInfo: semanticColors.fillInfo,
    fillSuccess: semanticColors.fillSuccess,
    fillWarning: semanticColors.fillWarning,
    fillError: semanticColors.fillError,
    fillErrorHover: semanticColors.fillErrorHover,
    fillErrorActive: semanticColors.fillErrorActive,
    fillErrorSecondary: semanticColors.fillErrorSecondary,
    fillErrorSecondaryHover: semanticColors.fillErrorSecondaryHover,
    fillErrorSecondaryActive: semanticColors.fillErrorSecondaryActive,
    fillEmphasis: semanticColors.fillEmphasis,
    fillEmphasisHover: semanticColors.fillEmphasisHover,
    fillEmphasisActive: semanticColors.fillEmphasisActive,

    // Text colors
    text: semanticColors.text,
    textSecondary: semanticColors.textSecondary,
    textDisabled: semanticColors.textDisabled,
    textInverse: semanticColors.textInverse,
    textPrimary: semanticColors.textPrimary,
    textAccent: semanticColors.textAccent,
    textBrand: semanticColors.textBrand,
    textInfo: semanticColors.textInfo,
    textSuccess: semanticColors.textSuccess,
    textWarning: semanticColors.textWarning,
    textError: semanticColors.textError,

    // Link colors
    link: semanticColors.link,
    linkHover: semanticColors.linkHover,
    linkActive: semanticColors.linkActive,
    linkDisabled: semanticColors.linkDisabled,

    // Icon colors
    icon: semanticColors.icon,
    iconSecondary: semanticColors.iconSecondary,
    iconDisabled: semanticColors.iconDisabled,
    iconInverse: semanticColors.iconInverse,
    iconPrimary: semanticColors.iconPrimary,
    iconAccent: semanticColors.iconAccent,
    iconBrand: semanticColors.iconBrand,
    iconInfo: semanticColors.iconInfo,
    iconSuccess: semanticColors.iconSuccess,
    iconWarning: semanticColors.iconWarning,
    iconError: semanticColors.iconError,

    // Border colors
    border: semanticColors.border,
    borderSecondary: semanticColors.borderSecondary,
    borderHover: semanticColors.borderHover,
    borderActive: semanticColors.borderActive,
    borderDisabled: semanticColors.borderDisabled,
    borderPrimary: semanticColors.borderPrimary,
    borderAccent: semanticColors.borderAccent,
    borderSecondaryAccent: semanticColors.borderSecondaryAccent,
    borderBrand: semanticColors.borderBrand,
    borderSecondaryInfo: semanticColors.borderSecondaryInfo,
    borderSecondarySuccess: semanticColors.borderSecondarySuccess,
    borderSecondaryWarning: semanticColors.borderSecondaryWarning,
    borderSecondaryError: semanticColors.borderSecondaryError,
    borderError: semanticColors.borderError,
    borderEmphasis: semanticColors.borderEmphasis,
    borderEmphasisHover: semanticColors.borderEmphasisHover,
    borderEmphasisActive: semanticColors.borderEmphasisActive,
    borderImage: semanticColors.borderImage,

    // Backdrop
    backdrop: semanticColors.backdrop,
  },
};
